import { ThemeProvider } from "@react-navigation/native";
import * as Sentry from "@sentry/react-native";
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
  focusManager,
} from "@tanstack/react-query";
import { useFonts } from "expo-font";
import { Stack, usePathname } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { useEffect, useMemo, useState } from "react";
import { AppState, Linking, useColorScheme } from "react-native";
import "react-native-reanimated";

import { useI18next } from "@luna-restaurant/i18n";
import { FontAwesomeKitLoader, ThemedToast } from "@luna-restaurant/ui";

import { ThemedHeaderContainer } from "@/components/ThemedHeaderContainer";
import { DarkTheme } from "@/constants/DarkTheme";
import { DefaultTheme } from "@/constants/DefaultTheme";
import { isApiError } from "@/hooks/useRequest";

Sentry.init({
  dsn: process.env.EXPO_PUBLIC_SENTRY_DSN,
  environment: process.env.EXPO_PUBLIC_SENTRY_ENVIRONMENT,

  // Adds more context data to events (IP address, cookies, user, etc.)
  // For more information, visit: https://docs.sentry.io/platforms/react-native/data-management/data-collected/
  sendDefaultPii: true,

  // uncomment the line below to enable Spotlight (https://spotlightjs.com)
  // spotlight: __DEV__,
});

export const unstable_settings = {
  anchor: "(auth)",
};

export default Sentry.wrap(function RootLayout() {
  const colorScheme = useColorScheme();
  const pathname = usePathname();
  const [fontsLoaded, fontsError] = useFonts({
    PoppinsRegular: require("@/assets/fonts/Poppins-Regular.ttf"),
    PoppinsMedium: require("@/assets/fonts/Poppins-Medium.ttf"),
    PoppinsBold: require("@/assets/fonts/Poppins-Bold.ttf"),
    BrushUpRegular: require("@/assets/fonts/Brush-Up-W01-Regular.ttf"),
  });
  const [i18nextLoaded, i18nextError] = useI18next({
    resources: {
      "de-DE": {
        translation: require("@/assets/translations/de.json"),
      },
    },
    lng: "de-DE",
    fallbackLng: "de-DE",
    supportedLngs: ["de-DE"],
    interpolation: {
      escapeValue: false,
    },
    compatibilityJSON: "v4",
    pluralSeparator: "_",
  });
  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error, query) => {
            if (
              error.message === "SessionExpired" ||
              error.message === "Unauthorized"
            ) {
              return;
            }
            if (isApiError(error) && error.status === 401) {
              return;
            }

            const apiContext = isApiError(error)
              ? {
                  method: error.method,
                  url: error.url,
                  status: error.status,
                  serverTitle: error.serverTitle,
                }
              : undefined;

            Sentry.captureException(error, {
              tags: { source: "react-query" },
              contexts: {
                query: {
                  queryKey: JSON.stringify(query.queryKey),
                  ...(apiContext ?? {}),
                },
              },
              extra: isApiError(error)
                ? { responseBody: error.responseBody }
                : undefined,
            });
          },
        }),
        defaultOptions: {
          queries: {
            staleTime: 5 * 60 * 1000, // Default HTTP Cache-Control Max-Age sent by Backend API
            retry: (failureCount, error) => {
              if (
                error.message === "SessionExpired" ||
                error.message === "Unauthorized"
              ) {
                return false;
              }
              if (
                isApiError(error) &&
                error.status >= 400 &&
                error.status < 500
              ) {
                return false;
              }
              return failureCount < 3;
            },
          },
        },
      }),
  );

  useEffect(() => {
    const subscription = AppState.addEventListener("change", (status) => {
      focusManager.setFocused(status === "active");
    });

    return () => {
      subscription.remove();
    };
  }, []);

  const theme = useMemo(
    () => (colorScheme === "dark" ? DarkTheme : DefaultTheme),
    [colorScheme],
  );

  if (fontsError) {
    console.error("fonts loading error:", fontsError);
  }

  if (i18nextError) {
    console.error("i18n error:", i18nextError);
  }

  if (!i18nextLoaded || !fontsLoaded) {
    return null;
  }

  // Determine active navigation based on current route
  const getActiveNavigation = (path: string) => {
    if (path.includes("/speiseplan")) return "speiseplan";
    if (path.includes("/schliesszeiten")) return "schliesszeiten";
    if (path.includes("/gastbestellungen")) return "gastbestellungen";
    if (path.includes("/lunchpakete")) return "lunchpakete";
    if (path.includes("/aktuelle-listen")) return "aktuelle-listen";
    return "speiseplan"; // default
  };

  const activeNavigation = getActiveNavigation(pathname);

  const handleLoginCustomAction = () => {
    Linking.openURL("https://luna.de/");
  };

  return (
    <QueryClientProvider client={queryClient}>
      <FontAwesomeKitLoader id={process.env.EXPO_PUBLIC_FONTAWESOME_KIT_ID}>
        <ThemeProvider value={theme}>
          <Stack>
            <Stack.Screen
              name="index"
              options={{
                header: () => (
                  <ThemedHeaderContainer
                    language="de"
                    variant="login"
                    onCustomAction={handleLoginCustomAction}
                    customActionLabel="header.lunaHome"
                  />
                ),
              }}
            />
            <Stack.Screen
              name="(auth)"
              options={{
                header: () => (
                  <ThemedHeaderContainer
                    language="de"
                    variant="authenticated"
                    activeNavigation={activeNavigation}
                  />
                ),
              }}
            />
          </Stack>
          <StatusBar style="auto" />
          <ThemedToast />
        </ThemeProvider>
      </FontAwesomeKitLoader>
    </QueryClientProvider>
  );
});
